import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale'
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import GlassCard from '../../../shared/components/card/GlassCard';
import { useAuth } from '../context/AuthContext';
import { useDate } from '../pages/Layout';
import { storageService } from '../service/storage';
import DotGrid from './DotGrid';

const BienvenidoForm = () => {
  const { getVentasPorDia, getVentasPorHora } = useAuth();
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [hourlyData, setHourlyData] = useState([]);
  const { currentDate, setIsLoading } = useDate();

  const fetchData = async () => {
    setLoading(true);
    setIsLoading(true);
    setError(null);
    const startTime = Date.now();

    try {
      const fecha = format(currentDate, 'yyyy-MM-dd');
      
      const [hourResponse, dayResponse] = await Promise.all([
        getVentasPorHora(fecha),
        getVentasPorDia(fecha)
      ]);

      setHourlyData(hourResponse.data || []);
      const ventaDelDia = dayResponse.data?.find(item => item.fecha === fecha);
      
      setName(storageService.getUserData()?.fullName || "");
      setSalesData(ventaDelDia || null);
      
    } catch (err) {
      console.error('Error cargando ventas:', err);
      setError('No se pudieron cargar los datos');
      setSalesData(null);
    } finally {
      const elapsed = Date.now() - startTime;
      const minDelay = 1000;
      
      setTimeout(() => {
        setLoading(false);
        setIsLoading(false);
      }, Math.max(0, minDelay - elapsed));
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  // ✅ MAPEOS COMPLETOS de todos los campos del objeto
  const dailySales = salesData?.pvp ?? 0;
  const transactions = salesData?.operaciones ?? 0;
  const totalImport = salesData?.importe ?? 0;
  const totalUnits = salesData?.cantidad ?? 0;                    // NUEVO: Unidades totales
  const avgTicket = salesData?.ticketMedio ?? 0;
  const avgTicketImporte = salesData?.ticketMedioImporte ?? 0;    // NUEVO: Ticket medio por importe
  const avgTicketCant = salesData?.ticketMedioCantidad ?? 0;
  const fechaData = salesData?.fecha ?? format(currentDate, 'yyyy-MM-dd'); // Fecha del objeto
  
  // ✅ Tendencias
  const salesTrend = salesData?.porcentajePvp ?? 0;
  const transactionsTrend = salesData?.porcentajeOperaciones ?? 0;
  const importTrend = salesData?.porcentajeImporte ?? 0;
  // Nota: El JSON no incluye porcentajes para cantidad ni ticketMedioImporte

  const isPositiveTrend = salesTrend >= 0;

  return (
    <div className='dashboard-container'>
      <GlassCard maxWidth='100%'>
        <header className="dashboard-header">
          <div className="welcome-section">
            <div className="welcome-text">
              <h1>{name ? `¡Buenos días, ${name}!` : '¡Buenos días!'}</h1>
              <p className="welcome-date">
                {format(currentDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })
                .replace(/(?<=^|\s)\S/g, c => c.toUpperCase())
                .replace(/\bDe\b/g, 'de')}  
              </p>
            </div>
            
            <div className="daily-stats-card">
              {loading ? (
                <div className="stats-content frosted-pulse">
                  <div className="loading-overlay">
                    <DotGrid />
                  </div>
                  <div className="blurred-content">
                    <div className="main-stat">
                      <span className="stat-label">Ventas Totales (PVP)</span>
                      <div className="stat-value">
                        <span className="amount">0,00</span>
                        <span className="currency">€</span>
                      </div>
                    </div>
                    <DotGrid />
                  </div>
                </div>
                
              ) : error ? (
                <div className="stats-content frosted-error">
                  <div className="error-overlay">
                    <div className="error-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                    <span className="error-text">{error}</span>
                    <button className="retry-btn" onClick={fetchData}>
                      Reintentar
                    </button>
                  </div>
                  <div className="blurred-content">
                    <div className="main-stat">
                      <span className="stat-label">Ventas Totales (PVP)</span>
                      <div className="stat-value">
                        <span className="amount">----</span>
                        <span className="currency">€</span>
                      </div>
                    </div>
                  </div>
                </div>
                
              ) : (
                <>
                  <div className="stats-content">
                    <div className="main-stat">
                      <span className="stat-label">Ventas Totales (PVP)</span>
                      <div className="stat-value">
                        <span className="amount">
                          {dailySales.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </span>
                        <span className="currency">€</span>
                      </div>
                      
                      <div className={`stat-trend ${isPositiveTrend ? 'positive' : 'negative'}`}>
                        <span className="trend-icon">{isPositiveTrend ? '↑' : '↓'}</span>
                        <span>{Math.abs(salesTrend).toFixed(1)}% vs período anterior</span>
                      </div>
                    </div>

                    <div className="mini-chart">
                      <ResponsiveContainer width="100%" height={60}>
                        <AreaChart data={Array.isArray(hourlyData) ? hourlyData : []}>
                          <defs>
                            <linearGradient id="miniGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#1397C5" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#1397C5" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area
                            type="monotone"
                            dataKey="pvpAcumulado"
                            stroke="#1397C5"
                            strokeWidth={2}
                            fill="url(#miniGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* ✅ QUICK STATS ACTUALIZADOS con todos los campos */}
                  <div className="quick-stats">
                    <div className="quick-stat-item">
                      <span className="quick-label">Operaciones</span>
                      <span className="quick-value">{transactions}</span>
                      {transactionsTrend !== 0 && (
                        <small className={`stat-trend ${transactionsTrend >= 0 ? 'positive' : 'negative'}`}>
                          {transactionsTrend > 0 ? '+' : ''}{transactionsTrend.toFixed(1)}%
                        </small>
                      )}
                    </div>
                    
                    <div className="quick-stat-divider"></div>
                    
                    {/* NUEVO: Campo cantidad (Unidades totales) */}
                    <div className="quick-stat-item">
                      <span className="quick-label">Unidades</span>
                      <span className="quick-value">{totalUnits}</span>
                    </div>
                    
                    <div className="quick-stat-divider"></div>
                    
                    <div className="quick-stat-item">
                      <span className="quick-label">Importe Total</span>
                      <span className="quick-value">
                        {totalImport.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}€
                      </span>
                      {importTrend !== 0 && (
                        <small className={`stat-trend ${importTrend >= 0 ? 'positive' : 'negative'}`}>
                          {importTrend > 0 ? '+' : ''}{importTrend.toFixed(1)}%
                        </small>
                      )}
                    </div>
                    
                    <div className="quick-stat-divider"></div>
                    
                    {/* ACTUALIZADO: Ahora muestra los 3 tickets medios */}
                    <div className="quick-stat-item tickets-medios-item">
                      <span className="quick-label">Tickets Medios</span>
                      <div className="tickets-vertical">
                        <div className="ticket-line">
                          <span className="ticket-name">PVP</span>
                          <span className="ticket-val">
                            {avgTicket.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}€
                          </span>
                        </div>
                        <div className="ticket-line">
                          <span className="ticket-name">Importe</span>
                          <span className="ticket-val">
                            {avgTicketImporte.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}€
                          </span>
                        </div>
                        <div className="ticket-line">
                          <span className="ticket-name">Unidades</span>
                          <span className="ticket-val">
                            {avgTicketCant.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
      </GlassCard>
    </div>
  );
};

export default BienvenidoForm;